//Built-in libraries

//Third party libraries
import { ClipboardDocumentCheckIcon, ClipboardDocumentListIcon, IdentificationIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { postApplication } from '@/api/Rover/ApplicationForm';
import type { IGetApplicationFormInfoServiceRes, IPostApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import SubmitSuccess from '@/components/Applications/SubmitSuccess';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
//Custom components
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
//Services
import { ApplicationFormProvider, useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import DefaultApplicantInformation from '../../Components/DefaultApplicantInformation';
import Application from './ApplicationInformation';
import HouseholdIncome from './HouseholdIncome';
import Review from './Review';

//STP Libraries

import DepartmentOfHousingLogo from '@/images/logos/department-of-housing.png';
import EqualHousingLogo from '@/images/logos/equal-housing.png';
import HayttsvilleLogo from '@/images/logos/hayttsville.jpg';
//Assets and Miscellaneous
import { initialValues } from './constants';
import { defaultStepsSchema, fakeSchema, step1Schema, step2Schema } from './validationSchema';

//Component Begin

const steps = [
  {
    name: 'Applicant Information',
    sectionId: 'applicantInformation',
    Icon: IdentificationIcon,
    component: <DefaultApplicantInformation />,
    schema: defaultStepsSchema,
  },
  {
    name: 'Head of Household Information',
    sectionId: 'application',
    Icon: ClipboardDocumentListIcon,
    component: <Application />,
    schema: step1Schema,
  },
  {
    name: 'Household Income',
    sectionId: 'householdIncome',
    Icon: UserGroupIcon,
    component: <HouseholdIncome />,
    schema: step2Schema,
  },
  {
    name: 'Review',
    sectionId: 'review',
    Icon: ClipboardDocumentCheckIcon,
    component: <Review />,
    schema: fakeSchema,
  },
];

interface IApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const ApplicationFormBase = ({ formData }: IApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } = useApplicationFormContext();

  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: () => {
      setIsSubmitSuccess(true);
    },
  });

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: data,
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return <SubmitSuccess message='' />;
  }

  return (
    <div className='container mx-auto'>
      <h2>{formData.data.name}</h2>
      <div className='flex mb-10'>
        <aside className='w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md'>
          <div className='w-full flex justify-center'>
            <img src={formData?.data.logo} alt='Form Logo' className='h-14 xl:h-20 object-scale-down' />
          </div>
          <nav className='space-y-1 py-5'>
            {steps.map(({ name, Icon }, index) => (
              <button
                key={index}
                className={`${
                  currentStep === index ? 'bg-neutralColor-lightest text-primaryColor-normal' : 'text-neutralColor-normal'
                } group text-left rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
              >
                <Icon className='shrink-0 -ml-1 mr-3 h-6 w-6' />
                {name}
              </button>
            ))}
          </nav>
        </aside>
        <div className='w-full bg-gray-50'>
          <HookFormProvider defaultValues={initialValues} mode='onChange' resolver={yupResolver(steps[currentStep].schema)}>
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className='flex gap-4 justify-end p-6'>
                  <Button
                    label='Previous'
                    btnStyle='neutral'
                    type='button'
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${submitApplication.isLoading ? 'Submitting...' : 'Submit'}`}
                    btnStyle='primary'
                    type='submit'
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
      <div className='flex justify-center'>
        <p className='text-left w-2/3 xl:w-1/2'>
          Submission of an application does not constitute acceptance to the program and/or reservation of funding. Awards are contingent
          upon successful submission of required documents, adherence to program guidelines and/or available funds.
        </p>
      </div>
      <div className='flex py-12'>
        <div className='basis-1/3'>
          <img className='object-contain h-24' src={HayttsvilleLogo} />
        </div>
        <div className='basis-1/3 flex justify-center'>
          <img className='h-24' src={DepartmentOfHousingLogo} />
        </div>
        <div className='basis-1/3 flex justify-end'>
          <img className='h-24' src={EqualHousingLogo} />
        </div>
      </div>
    </div>
  );
};

export const ApplicationForm = ({ ...props }: IApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
